import React, {useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import axios from "axios";
import {useRecoilState, useRecoilValue} from "recoil";
import {
  netPriceState,
  imageCountState,
  framePricesState,
  defaultFramePriceState,
  shippingPriceState,
  handlingFeeState,
  creditsAppliedState,
  creditsBalanceState
} from "@atoms/priceCalc";
import { selectedShippingAddress, validCoupon, addressLinkState, imageFrameState, personalAreaAuthState, giftCheckoutState } from "@atoms";
import { getPrices } from "../../../api/pricingService";
import { getCreditsBalance, applyCredits, removeCredits } from "@api";
import { useSecondModal } from "@helpers/hooks/useSecondModal";
import "./style.css";
import useDeliveryRange from "@helpers/hooks/useDeliveryRange";
import useGooglePay from "@helpers/hooks/useGooglePay";
import { rememberOrderAccess } from "@helpers/utils/orderAccess";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import InputCoupon from "../../components/InputCoupon";
import AmountCharged from "../../../helpers/AmountCharged";
import {imagesBlobState} from "@atoms";
import AddressLink from "../AddressLink";
import { getAbVariant, trackAbExposure } from "../../../api/visitTracking";
import { completeGiftCardOrder } from "../../../api/giftcard";

// Split test: the סה"כ amount in Rubik (today) vs Ploni Yad. Sticky per visitor,
// 50/50. Exposure is logged when the drawer actually opens; the arm is stamped on
// the cart so the admin CRO page can attribute purchases per arm.
const CART_TOTAL_FONT_EXPERIMENT = "cartTotalFont";
const CART_TOTAL_FONT_ARMS = ["rubik", "ploni"];

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to check if address is complete
const isAddressComplete = (address) => {
  if (!address) return false;
  
  // Helper function to check if a string field is valid (not empty after trimming)
  const isValidField = (field) => {
    return typeof field === 'string' && field.trim().length > 0;
  };
  
  // Check if number fields are valid
  const isValidNumber = (field) => {
    return field !== undefined && field !== null && field !== '';
  };
  
  // Check all required fields
  return (
    isValidField(address.fullName) && 
    isValidField(address.phoneNumber) && 
    isValidField(address.email) && 
    isValidField(address.address) && 
    isValidField(address.addressDetails) && 
    // zipCode is now optional
    isValidField(address.city)
  );
};

const Checkout = (props) => {
  const netPrice = useRecoilValue(netPriceState);
  const imagesBlob = useRecoilValue(imagesBlobState);
  const imageCount = useRecoilValue(imageCountState);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const selectedCoupon = useRecoilValue(validCoupon);
  const frameChoose = useRecoilValue(imageFrameState);
  const [framePrices, setFramePrices] = useRecoilState(framePricesState);
  const [defaultFramePrice, setDefaultFramePrice] = useRecoilState(defaultFramePriceState);
  const shippingPrice = useRecoilValue(shippingPriceState);
  const handlingFee = useRecoilValue(handlingFeeState);
  const [auth, setAuth] = useRecoilState(personalAreaAuthState);
  const [creditsApplied, setCreditsApplied] = useRecoilState(creditsAppliedState);
  const [creditsBalance, setCreditsBalance] = useRecoilState(creditsBalanceState);

  // GIFT-CARD mode: the drawer charges the server-priced gift amount against
  // the GC- order — no address/shipping/coupon/credits, order pre-created.
  const [giftCheckout, setGiftCheckout] = useRecoilState(giftCheckoutState);
  const giftMode = !!giftCheckout;

  const { show , handleCloseModal } = props;
  const modal = useSecondModal();

  const totalFontVariant = getAbVariant(CART_TOTAL_FONT_EXPERIMENT, CART_TOTAL_FONT_ARMS);
  useEffect(() => {
    // Gift-card checkout reuses this drawer but is a different funnel — keep it out.
    if (show && !giftMode) {
      trackAbExposure(CART_TOTAL_FONT_EXPERIMENT, totalFontVariant, localStorage.getItem("sessionId"));
    }
  }, [show, giftMode]); // eslint-disable-line react-hooks/exhaustive-deps -- totalFontVariant is sticky per visitor

  // Google Pay (Tranzila Hosted Fields) — desktop cart express button. Apple Pay is
  // not offered on desktop, so Google Pay shows on its own when eligible. Preloads
  // order + handshake so the tap charges synchronously (Google Pay needs the gesture).
  const { googlePayEligible, chargeGooglePay } = useGooglePay();
  const [gpayOrderId, setGpayOrderId] = useState(null);
  const [gpayThtk, setGpayThtk] = useState(null);

  const [numberOfImages,] = useState(5);
  const [creditsLoading, setCreditsLoading] = useState(false);

  const navigate = useNavigate();

  // Get the current frame type from localStorage or use the one from Recoil state
  const currentFrameType = frameChoose || localStorage.getItem("frameSelected") || "classic";

  // Delivery date picks the lead-time for the selected frame (falls back to default).
  const { cartDate } = useDeliveryRange(currentFrameType);

  // Get the price for the current frame type, or fall back to the default price
  const framePrice = framePrices[currentFrameType] || defaultFramePrice;
  
  // Frame type mapping for display
  const frameTypeDisplay = {
    "classic": { text: "קלאסי", icon: "/assets/file/images/d1.png" },
    "bold": { text: "נועז", icon: "/assets/file/images/d2.png" },
    "ever": { text: "נקי", icon: "/assets/file/images/d3.png" },
    "clean": { text: "אותנטי", icon: "/assets/file/images/d4.png" },
    "village": { text: "כפרי", icon: "/assets/file/images/d5.png" },
    "nordic": { text: "נורדי", icon: "/assets/file/images/d6.png" }
  };
  
  const currentFrameDisplay = frameTypeDisplay[currentFrameType] || frameTypeDisplay["classic"];
  
  const amountBeforeCredits = giftMode ? giftCheckout.amount : AmountCharged(
    imageCount + imagesBlob.length,
    numberOfImages,
    netPrice + (imagesBlob.length * framePrice),
    shippingPrice,
    selectedCoupon,
    handlingFee,
    0
  );
  const amountCharged = giftMode ? giftCheckout.amount : AmountCharged(
    imageCount + imagesBlob.length,
    numberOfImages,
    netPrice + (imagesBlob.length * framePrice),
    shippingPrice,
    selectedCoupon,
    handlingFee,
    creditsApplied
  );

  // Gift mode needs no shipping address — treat it as satisfied everywhere.
  const addressOk = giftMode || isAddressComplete(selectedAddress);

  // A gift card in the coupon slot that covers the whole photo order: nothing to
  // pay, so the main button completes the order instead of opening payment.
  const giftCovers = !giftMode && !!selectedCoupon?.giftCard && amountCharged === 0;
  const [giftOrderBusy, setGiftOrderBusy] = useState(false);
  const [giftOrderError, setGiftOrderError] = useState("");
  const handleGiftCardOrder = async () => {
    if (giftOrderBusy) return;
    setGiftOrderBusy(true);
    setGiftOrderError("");
    try {
      rememberOrderAccess(); // the success page needs these after localStorage.clear()
      const oid = await completeGiftCardOrder({
        ...selectedAddress,
        uid: localStorage.getItem("uniqueUserId"),
        sessionId: localStorage.getItem("sessionId"),
        imagesPayed: imageCount + imagesBlob.length,
        frameType: currentFrameType,
      });
      localStorage.clear();
      navigate(`/payment-success/${oid}`);
    } catch (e) {
      setGiftOrderBusy(false);
      setGiftOrderError(e?.response?.data?.error || "לא הצלחנו להשלים את ההזמנה, נסו שוב");
    }
  };

  // Drawer closed without paying → leave gift mode so the photo flow never
  // inherits the GC- order id or the gift amount.
  useEffect(() => {
    if (!show && giftMode) {
      setGiftCheckout(null);
      const oid = localStorage.getItem("orderId");
      if (oid && oid.startsWith("GC-")) localStorage.removeItem("orderId");
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // Credits can't reduce the shipping portion — cap by the post-coupon, non-shipping subtotal
  const shippingChargedPortion = (imageCount + imagesBlob.length) >= numberOfImages ? 0 : shippingPrice;
  const maxCreditEligible = Math.max(amountBeforeCredits - shippingChargedPortion - 2, 0);

  // Restore auth from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('pa_token');
    const savedPhone = localStorage.getItem('pa_phone');
    if (savedToken && savedPhone && !auth.isVerified) {
      setAuth({ isVerified: true, phoneNumber: savedPhone, token: savedToken });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Google Pay: preload order + handshake token (thtk) once eligible, so the tap can
  // fire chargeGpay synchronously (Google Pay requires the user gesture). Mirrors the
  // Apple Pay preload used on mobile.
  useEffect(() => {
    const preload = async () => {
      if (!googlePayEligible || !addressOk || gpayThtk || giftCovers) return;
      try {
        // Gift mode: the GC- order already exists server-side — just handshake.
        if (giftMode) {
          const hs = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${giftCheckout.amount}`);
          if (hs.data.status === 200 && hs.data.token) {
            setGpayOrderId(giftCheckout.giftOrderId);
            setGpayThtk(hs.data.token);
          }
          return;
        }
        rememberOrderAccess(); // the success page needs these after localStorage.clear()
        let orderId = localStorage.getItem("orderId");
        if (orderId) {
          const checkRes = await axios.get(`${BASE_URL}/user/checkorder/${orderId}`);
          if (!checkRes.data || checkRes.data.orderStatus !== OrderStatus.NOT_PAID) {
            localStorage.removeItem("orderId");
            orderId = null;
          }
        }
        if (!orderId) {
          const orderData = await axios.post(`${BASE_URL}/gama-createorder`, {
            ...selectedAddress,
            uid: localStorage.getItem('uniqueUserId'),
            sessionId: localStorage.getItem('sessionId'),
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType
          });
          if (!orderData.data || !orderData.data.oid) return;
          orderId = orderData.data.oid;
          localStorage.setItem("orderId", orderId);
        }
        const handshakeResponse = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amountCharged}`);
        if (handshakeResponse.data.status === 200 && handshakeResponse.data.token) {
          setGpayOrderId(orderId);
          setGpayThtk(handshakeResponse.data.token);
        }
      } catch (e) {
        console.error("Checkout: Google Pay preload failed", e);
      }
    };
    preload();
  }, [googlePayEligible, selectedAddress, amountCharged, imageCount, imagesBlob.length, currentFrameType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Google Pay charge — synchronous from the tap; on success marks the order paid +
  // navigates to the success page, exactly like the mobile flow.
  const handleGooglePay = () => {
    if (!gpayOrderId || !gpayThtk) {
      console.warn("Checkout: Google Pay not ready yet");
      return;
    }
    const framesCount = giftMode ? giftCheckout.frames : imageCount + imagesBlob.length;
    const frameLabel = (frameTypeDisplay[currentFrameType] || frameTypeDisplay["classic"]).text;
    const invoiceProductDesc = giftMode
      ? `גיפט קארד בלנדס ×${framesCount}`
      : `תמונות ממוסגרות – ${frameLabel} ×${framesCount}`;

    chargeGooglePay({
      terminal_name: "blends",
      response_language: 'english',
      currency_code: "ILS",
      amount: amountCharged,
      tran_mode: "A",
      thtk: gpayThtk,
      pdesc: invoiceProductDesc,
      contact: giftMode ? giftCheckout.buyerName : selectedAddress.fullName,
      email: (giftMode ? giftCheckout.buyerEmail : selectedAddress.email) || "",
      phone: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
      address: giftMode ? "" : selectedAddress.address,
      city: giftMode ? "" : selectedAddress.city,
      uniqueid: gpayOrderId,
      notify_url: `${window.location.origin}/api/google-pay-webhook?orderId=${gpayOrderId}&method=google_pay`,
    }, (err, response) => {
      if (err) {
        console.error("Checkout: Google Pay error", err);
        return;
      }
      const isSuccess = response?.transaction_response?.success === true || response?.status === "ok";
      const last4Digits = response?.transaction_response?.credit_card_last_4_digits;
      if (isSuccess) {
        if (giftMode) {
          // The webhook GC- branch activates the card; just route to success.
          const gid = gpayOrderId;
          setGiftCheckout(null);
          localStorage.removeItem("orderId");
          navigate(`/gift-success/${gid}`);
          return;
        }
        axios.post(`${BASE_URL}/user/changeorderstatus/${gpayOrderId}`, {
          status: OrderStatus.PAID,
          amountCharged: amountCharged,
          last4Digits: last4Digits,
          paymentMethod: 'google_pay'
        }).catch(e => console.error("Error updating order status:", e));
        const oid = gpayOrderId;
        localStorage.clear();
        navigate(`/payment-success/${oid}`);
      }
    });
  };

  // Fetch prices when component mounts
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const prices = await getPrices();
        if (prices && prices.length > 0) {
          const priceMap = {};
          prices.forEach(item => {
            priceMap[item.frameType] = item.price;
          });
          setFramePrices(priceMap);
        }
      } catch (error) {
        console.error('Error fetching frame prices:', error);
      }
    };

    fetchPrices();
  }, [setFramePrices]);

  // Fetch credits balance and auto-apply if eligible.
  // Gated on cart hydration: without this, the effect can fire with the
  // atom defaults (netPrice=0, imageCount=0) and cap maxApplicable at
  // shippingPrice-2 instead of the real cart total.
  const totalImages = imageCount + imagesBlob.length;
  const totalNetPrice = netPrice + (imagesBlob.length * framePrice);
  const isCartHydrated = totalImages > 0 && totalNetPrice > 0;
  useEffect(() => {
    if (giftMode) return; // gift cards: no Moments+ credits
    if (auth.isVerified && auth.token && creditsApplied === 0 && isCartHydrated) {
      getCreditsBalance(auth.token)
        .then(res => {
          if (!res.success || res.balance <= 0) return;
          const sessionId = localStorage.getItem('sessionId');
          const maxApplicable = Math.min(res.balance, maxCreditEligible);
          if (sessionId && maxApplicable > 0) {
            setCreditsApplied(maxApplicable);
            setCreditsBalance(res.balance - maxApplicable);
            applyCredits(auth.token, sessionId, maxApplicable)
              .then(r => {
                if (r.success) { setCreditsApplied(r.applied); setCreditsBalance(r.remainingBalance); }
                else { setCreditsApplied(0); setCreditsBalance(res.balance); }
              })
              .catch(() => { setCreditsApplied(0); setCreditsBalance(res.balance); });
          } else {
            setCreditsBalance(res.balance);
          }
        })
        .catch(() => {});
    }
  }, [auth.isVerified, auth.token, isCartHydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleCredits = async () => {
    if (!auth.token) return;
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) return;

    if (creditsApplied > 0) {
      // Removing: show spinner on cancel button, then clear
      setCreditsLoading(true);
      try {
        const res = await removeCredits(auth.token, sessionId);
        if (res.success) {
          setCreditsApplied(0);
          setCreditsBalance(res.balance);
        }
      } catch (err) {
        console.error('Credits error:', err?.response?.status, err?.response?.data || err);
      } finally {
        setCreditsLoading(false);
      }
    } else {
      // Applying: optimistic — show applied state immediately, confirm in background
      const maxApplicable = Math.min(creditsBalance, maxCreditEligible);
      if (maxApplicable <= 0) return;
      setCreditsApplied(maxApplicable);
      setCreditsBalance(creditsBalance - maxApplicable);
      try {
        const res = await applyCredits(auth.token, sessionId, maxApplicable);
        if (res.success) {
          setCreditsApplied(res.applied);
          setCreditsBalance(res.remainingBalance);
        } else {
          // Revert on failure
          setCreditsApplied(0);
          setCreditsBalance(creditsBalance);
        }
      } catch (err) {
        console.error('Credits error:', err?.response?.status, err?.response?.data || err);
        setCreditsApplied(0);
        setCreditsBalance(creditsBalance);
      }
    }
  };

  const [showAddressLink, setShowAddressLink] = useRecoilState(addressLinkState);

  const openAddCard = (name, card = false) => {
    if((!selectedAddress || !isAddressComplete(selectedAddress)) && card && !giftMode){
      return setShowAddressLink(true);
    }
    modal("open", name);
  };

  const renderCounts = () => {
    if (giftMode) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>🎁</span>
          <span>x{giftCheckout.frames}</span>
          <span>גיפט קארד בלנדס</span>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img src={currentFrameDisplay.icon} alt={currentFrameDisplay.text} style={{ width: "20px", height: "20px", objectFit: "contain" }} />
        <span>x{imageCount + imagesBlob.length}</span>
        <span>בלנדס</span>
        <span>{currentFrameDisplay.text} - גודל 21x21</span>
      </div>
    )
  }

  return (
    <div>
      <div
        id="myCart"
        className="cart_sidebar"
        style={{ left: show ? "0px" : "-370px" }}
      >
        <AddressLink show={showAddressLink}/>
        <div className="checkout-card-wrapper">
          <div className="card-and-address-buttons">
            <button
              className={cn("", { checked: isAddressComplete(selectedAddress) })}
              style={{ fontSize: "16px", fontWeight: isAddressComplete(selectedAddress) ? 400 : 700, }}
              onClick={() => {
                openAddCard("addAddress");
              }}
            >
              {isAddressComplete(selectedAddress) ? (
                <>
                  {selectedAddress.fullName + ", " + selectedAddress.city}
                </>
              ) : (
                <>
                  {"הוספת פרטי משלוח"}
                </>
              )}
              {isAddressComplete(selectedAddress) ? (
                <img src="/assets/file/images/Check.png" style={{ marginBottom: "2px", marginRight: "8px" }} alt="" />
              ) : (
                <img src={"/assets/images/form_address.png"} style={{ width: "25px", height: "25px" }} alt="" />
              )}
            </button>
            <button
              style={{ fontSize: "16px", fontWeight: 700 }}
              onClick={() => openAddCard("selectCard", true)}
            >
              הוספת אמצעי תשלום
              <img src={"/assets/images/form_card.svg"} style={{ width: "25px", height: "25px" }} alt="" />
            </button>
          </div>

            <div className="checkout-calculation">
              <div className="checkout-calculation__info" style={{ marginBottom: "0px" }}>
               {imageCount + imagesBlob.length <= 4 && <div>
                                  <p style={{ fontWeight:400, fontSize: "16px"}}>
                                  המשלוח צפוי
                                  להגיע אליכם עד יום
                                  <span style={{ fontWeight: 500, direction: "ltr !important" }}>{cartDate}</span>
                                  </p>
                                  </div>}
                {imageCount + imagesBlob.length > 4 &&
                <div>
                <p style={{ fontWeight: 400, fontSize: "16px" }}>ההזמנה שלכם זכאית ל<span style={{ fontWeight: 500 }}>משלוח חינם,&nbsp;<br /></span>
                                  המשלוח צפוי
                                  להגיע אליכם עד יום
                <span style={{ fontWeight: 500, direction: "ltr !important" }}>{cartDate}</span>
                </p>
                </div>
                }
              <img src="/assets/images/checkout_check.svg" alt="check" style={{ width: "15px", height: "15px" }} />
            </div>
            {/* {(frameChoose === "classic" || frameChoose === "bold") && (
              <div className="frame-info-message" style={{ 
                backgroundColor: "#F8F3D6", 
                padding: "12px 15px", 
                borderRadius: "8px", 
                marginBottom: "15px", 
                marginTop: "12px",
                fontSize: "14px",
                fontWeight: "400",
                textAlign: "right",
                color: "#9C8558",
                border: "1px solid #9C8558"
              }}>
                {frameChoose === "classic" && (
                  <p style={{ margin: 0, direction: "rtl" }}>
                    ייתכן עיכוב של כשבוע בהגעת המשלוח בשל חוסר מלאי זמני במסגרת שבחרת.
                  </p>
                )}
                {frameChoose === "bold" && (
                  <p style={{ margin: 0, direction: "rtl" }}>
                    ייתכן עיכוב של כשבוע בהגעת המשלוח בשל חוסר מלאי זמני במסגרת שבחרת.
                  </p>
                )}
              </div>
            )} */}
            <InputCoupon imageCount={imageCount + imagesBlob.length} netPrice={netPrice + (imagesBlob.length * framePrice)} />
            <div className="price__table">
              <div className="price__table--row" style={{ fontWeight: "400" }}>
                <div>{giftMode ? giftCheckout.amount : netPrice + (imagesBlob.length * framePrice)}</div>
                <div style={{ direction: "rtl" }}>{renderCounts()}</div>
              </div>
              <div className="price__table--row" style={{ fontWeight: "400" }}>
                <div>{giftMode ? "0.00" : ((imageCount + imagesBlob.length) * handlingFee).toFixed(2)}</div>
                <div>דמי טיפול</div>
              </div>
              <div className="price__table--row" style={{ fontWeight: "400" }}>
                {giftMode && <div style={{ color: "#B12704" }}>₪&nbsp;0.00</div>}
                {!giftMode && imageCount + imagesBlob.length <= 4 && <div>{shippingPrice}</div>}
                {!giftMode && imageCount + imagesBlob.length > 4 && <div style={{ color: "#B12704" }}>₪&nbsp;0.00<span style={{ textDecoration: "line-through", color: "#999", marginLeft: "6px" }}>₪{shippingPrice}</span></div>}
                <div>משלוח</div>
              </div>

              {auth.isVerified && (creditsBalance > 0 || creditsApplied > 0) && creditsApplied === 0 && (
                <div className="price__table--credits">
                  <div className="credits-toggle">
                    <label className="credits-toggle__switch">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={handleToggleCredits}
                        disabled={creditsLoading}
                      />
                      <span className="credits-toggle__slider" />
                    </label>
                    <span className="credits-toggle__available">₪{creditsBalance.toFixed(2)} זמינים</span>
                  </div>
                  <div className="credits-toggle__label">
                    <img src="/assets/images/blends-moments-mascot.svg" alt="" className="credits-toggle__mascot" />
                    <span className="credits-toggle__brand-text">Moments+</span>
                  </div>
                </div>
              )}

              <div className={`price__table--row total${creditsApplied > 0 ? ' total--credits' : ''}`}>
                <div style={{ fontSize: "15px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", direction: "ltr" }}>
                  <span className={cn({
                    "total__discounted-price": creditsApplied > 0,
                    "total__amount--ploni": totalFontVariant === "ploni",
                  })}>
                    ₪{amountCharged.toFixed(2)}
                  </span>
                  {creditsApplied > 0 && (
                    <span className="total__original-price">₪{amountBeforeCredits.toFixed(2)}</span>
                  )}
                  {creditsApplied > 0 && (
                    <img src="/assets/images/blends-moments-mascot.svg" alt="" className="total__mascot" />
                  )}
                </div>
                <div style={{ fontSize: "15px", fontWeight: "700" }}>{'סה"כ'}</div>
              </div>

              {creditsApplied > 0 && (
                <div className={`credits-applied-row${creditsLoading ? ' is-loading' : ''}`}>
                  <img
                    src="/assets/images/blends-moments-mascot.svg"
                    alt=""
                    className="credits-applied-row__mascot"
                  />
                  <span className="credits-applied-row__text">
                    חסכת <strong>₪{creditsApplied.toFixed(2)}</strong> בהזמנה הזו עם Blends Moments+
                  </span>
                  <button
                    className="credits-applied-row__cancel"
                    onClick={handleToggleCredits}
                    disabled={creditsLoading}
                    title="בטל מימוש נקודות"
                  >
                    {creditsLoading ? <span className="credits-applied-row__spinner" /> : '\u00d7'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="checkout-button-wrapper">
            {!addressOk && (
              <button
                className="checkout-button"
                onClick={() => openAddCard("addAddress")}
              >
                בחירת כתובת למשלוח
              </button>
            )}
            {addressOk && (
              /* Google Pay REPLACES the generic button when armed (mirrors the mobile
                 Apple Pay pattern) — other methods stay reachable via the
                 "הוספת אמצעי תשלום" link above. Until the order+thtk preload lands,
                 keep the functional generic button (no disabled dead button). */
              giftCovers ? (
                <button
                  className="checkout-button"
                  onClick={handleGiftCardOrder}
                  disabled={giftOrderBusy}
                >
                  השלמת ההזמנה
                </button>
              ) : (googlePayEligible && gpayOrderId && gpayThtk) ? (
                <button
                  type="button"
                  className="gpay-express-btn"
                  onClick={handleGooglePay}
                  aria-label="תשלום עם Google Pay"
                >
                  <img src="https://www.gstatic.com/instantbuy/svg/light_gpay.svg" alt="Google Pay" />
                </button>
              ) : (
                <button
                  className="checkout-button"
                  onClick={() => openAddCard("selectCard")}
                >
                  בחירת אמצעי תשלום
                </button>
              )
            )}
            {giftCovers && giftOrderError && (
              <p style={{ color: "#B12704", fontSize: "14px", textAlign: "center", margin: "8px 0 0" }}>{giftOrderError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
